import { type JSX, useState } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.js';
import type { OneDriveScope } from '@/lib/api/onedrive.js';
import type { WorkspaceConnectorConnection } from '@/lib/api/workspace-connectors.js';

import { folderStanding } from './folder-coverage.js';
import { OneDriveFolderBrowser } from './OneDriveFolderBrowser.js';

/**
 * Adding a OneDrive folder from the Artifacts tab.
 *
 * All this owns is the question the connector dialog never has to ask: WHICH personal Microsoft
 * connection. Everything below that is {@link OneDriveFolderBrowser}, the same browser the connector
 * dialog mounts — so the path, the row states and what an Add grants are one thing to learn rather
 * than two. This screen used to be its own picker with its own search, its own "Back to OneDrive
 * root" and a two-step Select-then-name flow that no other surface had.
 *
 * The alias step went with it. A folder is added under its own name at full strength, the way it is
 * everywhere else, and renaming is the row's Settings once it is on the list — the same answer this
 * dialog already gave about access.
 */
export function OneDriveSetupDialog({
  open,
  workspaceId,
  connections,
  scopes,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  workspaceId: string;
  connections: readonly WorkspaceConnectorConnection[];
  /** The folders already granted, so the browser can mark what is on the list and what is inside it. */
  scopes: readonly OneDriveScope[];
  onOpenChange: (open: boolean) => void;
  onCreated: (scope: OneDriveScope) => void;
}): JSX.Element {
  const [connectionId, setConnectionId] = useState(
    connections[0]?.connectionId ?? '',
  );
  const granted = scopes.filter((scope) => scope.connectionId === connectionId);

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Add from OneDrive"
      description="Choose a folder or a single file of your personal OneDrive. It arrives with everything switched on — reading and writing, in chat and in automations — and narrowing is its Settings afterwards."
      className="flex h-[min(88vh,44rem)] max-h-[92vh] w-full max-w-2xl flex-col overflow-y-hidden"
    >
      <div className="flex min-h-0 flex-1 flex-col gap-3 pt-3">
        {connections.length === 0 ? (
          <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
            No reachable personal SharePoint connection is available. Open
            Connectors in your personal space and allow all workspaces, or open
            the workspace where the connection is available.
          </div>
        ) : (
          <>
            {/*
              Only where there is a choice. One connection is not a question, and asking it put a
              dropdown and a Browse button in front of the tree every single time.
            */}
            {connections.length > 1 && (
              <label className="flex shrink-0 flex-col gap-1 text-sm">
                Personal Microsoft connection
                <Select value={connectionId} onValueChange={setConnectionId}>
                  <SelectTrigger
                    aria-label="Personal Microsoft connection"
                    className="w-full"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {connections.map((connection) => (
                      <SelectItem
                        key={connection.connectionId}
                        value={connection.connectionId}
                      >
                        {connection.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </label>
            )}
            {/*
              Keyed by the connection so switching starts a fresh setup session against the drive
              that was chosen, rather than browsing the previous one with a new label above it.
            */}
            <OneDriveFolderBrowser
              key={connectionId}
              workspaceId={workspaceId}
              connectionId={connectionId}
              standingOf={(itemId, ancestors) =>
                folderStanding(granted, itemId, ancestors)
              }
              onAdded={(scope) => {
                onCreated(scope);
                onOpenChange(false);
              }}
            />
          </>
        )}
      </div>
    </AppDialog>
  );
}
